import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Award, Mail, Phone, MapPin, Clock, Euro, X } from "lucide-react";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuova", cls: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
  { value: "contacted", label: "Contattato", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  { value: "meeting_scheduled", label: "Meeting fissato", cls: "bg-purple-500/15 text-purple-500 border-purple-500/30" },
  { value: "offer_sent", label: "Offerta inviata", cls: "bg-cyan-500/15 text-cyan-500 border-cyan-500/30" },
  { value: "won", label: "Chiuso (won)", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  { value: "lost", label: "Perso", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
];
const meta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];

const CAPITAL_LABELS = {
  "30-50k": "€30-50k",
  "50-100k": "€50-100k",
  "100-200k": "€100-200k",
  "200k+": "€200k+",
};

function DetailModal({ req, onClose, onSaved }) {
  const [status, setStatus] = useState(req.status || "new");
  const [notes, setNotes] = useState(req.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/franchising/requests/${req.id}/status`, { status, admin_notes: notes });
      toast.success("Aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="franc-detail-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-background max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border rounded-sm">
        <div className="p-6 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">Franchising · Lead</div>
            <h2 className="editorial-heading text-2xl mt-1">{req.full_name}</h2>
            <div className="text-sm text-muted-foreground flex items-center gap-3 mt-1">
              <span><MapPin className="inline h-3 w-3" /> {req.city}, {req.country}</span>
              {req.has_premises && <span className="text-[10px] uppercase px-1.5 py-0.5 bg-emerald-500/15 text-emerald-500 rounded-sm">Locale disponibile</span>}
            </div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div><Mail className="inline h-3.5 w-3.5 text-muted-foreground" /> <a href={`mailto:${req.email}`} className="hover:underline">{req.email}</a></div>
            <div><Phone className="inline h-3.5 w-3.5 text-muted-foreground" /> <a href={`tel:${req.phone}`} className="hover:underline">{req.phone}</a></div>
            {req.available_capital && <div><Euro className="inline h-3.5 w-3.5 text-muted-foreground" /> Capitale: <strong>{CAPITAL_LABELS[req.available_capital] || req.available_capital}</strong></div>}
            <div><Clock className="inline h-3.5 w-3.5 text-muted-foreground" /> {new Date(req.created_at).toLocaleString("it-IT")}</div>
          </div>

          {req.business_experience && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Esperienza</div>
              <div className="bg-muted p-3 rounded-sm text-sm whitespace-pre-wrap">{req.business_experience}</div>
            </div>
          )}
          {req.motivation && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Motivazione</div>
              <div className="bg-muted p-3 rounded-sm text-sm whitespace-pre-wrap">{req.motivation}</div>
            </div>
          )}

          <div className="border-t border-border pt-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold">Stato lead</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="fr-detail-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold">Note interne</label>
              <Textarea data-testid="fr-detail-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Esito chiamata, follow-up programmato, dettagli rilevanti..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="fr-detail-save" onClick={save} disabled={busy} className="bg-[hsl(var(--primary))]">{busy ? "Salvo..." : "Salva"}</Button>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminFranchising() {
  const [data, setData] = useState({ requests: [], total: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const reload = () => api.get("/admin/franchising/requests").then((r) => setData(r.data)).catch(() => toast.error("Errore"));
  useEffect(() => { reload(); }, []);

  const filtered = filter === "all" ? data.requests : data.requests.filter((r) => r.status === filter);

  return (
    <div data-testid="admin-franchising-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Franchising</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3"><Award className="h-8 w-8 text-[hsl(var(--primary))]" />Richieste Franchising</h1>
      <p className="text-muted-foreground mt-2 mb-6">Aspiranti franchisee che hanno chiesto info da /franchising</p>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
        <button data-testid="fr-flt-all" onClick={() => setFilter("all")} className={`text-left p-3 rounded-sm border transition-colors ${filter === "all" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-card border-border"}`}>
          <div className="text-[10px] uppercase tracking-wider opacity-70">Totale</div>
          <div className="text-2xl font-bold">{data.total}</div>
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button key={s.value} data-testid={`fr-flt-${s.value}`} onClick={() => setFilter(s.value)} className={`text-left p-3 rounded-sm border transition-colors ${filter === s.value ? s.cls + " border-current" : "bg-card border-border"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</div>
            <div className="text-2xl font-bold">{data.by_status[s.value] || 0}</div>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Award className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna richiesta {filter !== "all" ? `${meta(filter).label.toLowerCase()}` : "ancora"}</div>
          <div className="text-sm text-muted-foreground mt-1">Le richieste da /franchising appariranno qui.</div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Lead</TableHead><TableHead>Città</TableHead><TableHead>Contatti</TableHead><TableHead>Capitale</TableHead><TableHead>Locale</TableHead><TableHead>Data</TableHead><TableHead>Stato</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const m = meta(r.status);
                return (
                  <TableRow key={r.id} data-testid={`fr-row-${r.id}`} onClick={() => setSelected(r)} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-semibold">{r.full_name}</TableCell>
                    <TableCell><MapPin className="inline h-3 w-3" /> {r.city} <span className="text-xs text-muted-foreground">({r.country})</span></TableCell>
                    <TableCell>
                      <div className="text-xs flex items-center gap-1"><Mail className="h-3 w-3 text-muted-foreground" />{r.email}</div>
                      <div className="text-xs flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{r.phone}</div>
                    </TableCell>
                    <TableCell className="text-xs">{r.available_capital ? CAPITAL_LABELS[r.available_capital] : "—"}</TableCell>
                    <TableCell>{r.has_premises ? <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-500 rounded-sm">SI</span> : <span className="text-xs text-muted-foreground">no</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</TableCell>
                    <TableCell><span className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm font-bold ${m.cls}`}>{m.label}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {selected && <DetailModal req={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); reload(); }} />}
    </div>
  );
}
